// The browser-host's persistent server-side KV, as a page-lived singleton.
//
// There is exactly ONE durable SQLite-WASM database on a player's machine:
// the OPFS-backed `.munos-server` store (the `munos_kv` table, at the 10 MB
// server quota). It lives inside a Web Worker because the OPFS SAHPool VFS
// needs FileSystemSyncAccessHandle, which browsers expose only off the main
// thread (see opfs-sqlite-store.ts / opfs-sqlite-worker.ts).
//
// That VFS is single-owner: only one SAHPool may hold a given directory at a
// time. So everything that touches the persistent server store — every
// LocalServer a tile stands up, AND the on-demand database admin panel —
// shares the ONE connection memoised here, rather than each opening its own
// worker and colliding on the file. The worker outlives any individual
// LocalServer (a fresh singleplayer launch reuses it), so it is never
// torn down for the life of the page.

import { InMemoryMunosKv, type MunosKvBackend } from "@multinostalgia/munos";
import { OpfsSqliteMunosKv } from "./opfs-sqlite-store.js";

// Server-tier quota + a DISTINCT OPFS directory from the client store
// (.munos-host), so the in-browser server's stash never collides with the
// client-side KV of any mod on the same machine (docs/persistence.md).
//
// This is a deliberately generous PER-(scope, namespace) budget, not a
// platform limit: the store is OPFS-backed (origin storage is GBs, not
// localStorage's ~5-10 MB wall), so the cap is pure policy. 50 MiB per level
// holds thousands of (suppression-thinned) runs while still bounding a runaway
// writer. The recorder surfaces a refused write via `event db_error` rather
// than dropping runs silently.
export const SERVER_QUOTA_BYTES = 50 * 1024 * 1024;
export const SERVER_OPFS_DIR = ".munos-server";

let kvPromise: Promise<MunosKvBackend> | null = null;
// Set only when the OPFS backend came up — the in-memory fallback can't run
// arbitrary SQL, so the admin panel needs to know which it got.
let sqlBackend: OpfsSqliteMunosKv | null = null;

// Get-or-create the shared persistent server KV. Memoised: concurrent callers
// (a tile launching, the admin panel opening) await the same construction.
// Falls back to an in-memory store — lost on reload — when the browser can't
// run the OPFS VFS, so persistence degrades rather than the feature breaking.
export function getSharedServerKv(): Promise<MunosKvBackend> {
  kvPromise ??= openServerKv();
  return kvPromise;
}

async function openServerKv(): Promise<MunosKvBackend> {
  try {
    return await createServerDb();
  } catch (e) {
    if (isCorruptionError(e)) {
      // The OPFS store opened but is INCONSISTENT — e.g. a half-applied
      // migration (table present, unrecorded) left by a reload that killed
      // the worker mid-write, or a genuinely malformed disk image. Unlike a
      // missing-OPFS browser or a cross-tab lock, this is repairable: wipe the
      // one broken store and reprovision from the migration suite. Whatever
      // was in it is already unreachable (the alternative is serving in-memory
      // and losing it anyway), so this only ever discards a store that was
      // lost regardless — and it saves the user from having to hand-clear OPFS.
      console.warn("[persistent-db] server store is corrupt; wiping and reprovisioning:", e);
      try {
        await OpfsSqliteMunosKv.wipe(SERVER_OPFS_DIR);
        return await createServerDb();
      } catch (e2) {
        console.error(
          "[persistent-db] recovery failed; server-side storage is in-memory (lost on reload):",
          e2,
        );
        return new InMemoryMunosKv(SERVER_QUOTA_BYTES);
      }
    }
    // NOT repairable by wiping — the browser can't run the OPFS VFS at all, or
    // another tab already owns this single-owner store. Wiping would be futile
    // (it'd fail again) or actively harmful (destroy the other tab's data), so
    // leave any existing store intact and degrade to in-memory.
    console.warn(
      "[persistent-db] OPFS-SQLite unavailable; server-side storage is in-memory (lost on reload):",
      e,
    );
    return new InMemoryMunosKv(SERVER_QUOTA_BYTES);
  }
}

async function createServerDb(): Promise<OpfsSqliteMunosKv> {
  const db = await OpfsSqliteMunosKv.create({
    quotaPerNamespace: SERVER_QUOTA_BYTES,
    opfsDirectory: SERVER_OPFS_DIR,
  });
  sqlBackend = db;
  return db;
}

// Does this init error mean the on-disk store is corrupt/inconsistent and can
// be fixed by wiping it? Conservative ALLOW-LIST: we only auto-destroy a store
// when the message positively indicates corruption. Anything unrecognised
// (missing OPFS, a cross-tab acquisition failure) is treated as non-repairable
// so we never wipe data on a merely transient or environmental failure.
function isCorruptionError(e: unknown): boolean {
  const msg = (e instanceof Error ? e.message : String(e)).toLowerCase();
  return (
    msg.includes("already exists") || // half-applied migration: table without its tracking row
    msg.includes("malformed") || // SQLITE_CORRUPT: database disk image is malformed
    msg.includes("not a database") || // SQLITE_NOTADB: header/pages trashed
    msg.includes("sqlite_corrupt") ||
    msg.includes("sqlite_notadb")
  );
}

// The SQL-queryable handle for the database admin panel. Resolves the shared
// connection first, then returns the OPFS store when one is live, or null on
// the in-memory fallback (nothing to browse — there's no real SQLite there).
export async function getServerSqlDb(): Promise<OpfsSqliteMunosKv | null> {
  await getSharedServerKv();
  return sqlBackend;
}
